import { Calendar, Mic, Paperclip, Pencil, Send, StopCircle, Trash2 } from "lucide-react";

import QuickMessageBlock from "../QuickMessageBlock";
import AttachmentList from "../AttachmentList";
import EmotesPanel from "../EmotesPanel";
import ScheduledMessagesList from "../ScheduleMessagesList";
import Dropzone from "../../Dropzone";
import { inputButtons } from "../../../config/inputButtons";
import { getFileType, getFileUrl } from "../../../utils/helpers";

const AttachmentPreview = ({ attachment, onEdit, onRemove }) => {
  const fileUrl = getFileUrl(attachment);
  const fileType = getFileType(attachment);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-green-300 bg-green-100 p-3 text-sm text-green-800 dark:border-green-700 dark:bg-green-900 dark:text-green-200">
      <div className="w-full">
        {fileType === "image" && (
          <img src={fileUrl} alt="preview" className="max-w-64 rounded-lg object-cover" />
        )}

        {fileType === "video" && (
          <video controls className="max-h-48 w-full rounded-lg">
            <source src={fileUrl} />
          </video>
        )}

        {fileType === "audio" && (
          <audio controls className="mb-2 w-full">
            <source src={fileUrl} />
          </audio>
        )}

        {fileType === "doc" && (
          <iframe src={fileUrl} className="h-48 w-full rounded-lg" title="Preview de documento" />
        )}

        {fileType === "other" && (
          <div className="flex items-center gap-2">
            <span>Arquivo não suportado para preview</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Paperclip />

        <div className="mr-auto flex flex-col overflow-hidden leading-tight">
          <span className="font-medium">Arquivo anexado</span>
          <span className="max-w-xs truncate text-xs opacity-80">{attachment.split("/").pop()}</span>
        </div>

        <button
          onClick={onEdit}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/60 dark:text-blue-400"
        >
          <Pencil className="h-5 w-5" />
        </button>

        <button
          onClick={onRemove}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/60 dark:text-red-400"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const ScheduleBadge = ({ scheduleAt, onEdit, onRemove }) => (
  <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-100 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900 dark:text-yellow-200">
    <Calendar />

    <div className="mr-auto flex flex-col leading-tight">
      <span className="font-medium">Mensagem agendada</span>
      <span className="text-xs opacity-80">
        {new Date(scheduleAt).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    </div>

    <button
      onClick={onEdit}
      className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/60 dark:text-blue-400 dark:hover:bg-blue-900"
    >
      <Pencil className="h-5 w-5" />
    </button>

    <button
      onClick={onRemove}
      className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-600 transition-colors hover:bg-red-200 dark:bg-red-900/60 dark:text-red-400 dark:hover:bg-red-900"
    >
      <Trash2 className="h-5 w-5" />
    </button>
  </div>
);

const QuotedPreview = ({ message, contactName, onRemove }) => (
  <div className="mb-3">
    <div className="flex items-start gap-3 rounded-lg border-l-4 border-green-500 bg-gray-50 p-3 dark:bg-gray-900">
      <div className="flex-1 overflow-hidden">
        <p className="text-xs font-semibold text-green-600 dark:text-green-400">
          Respondendo a {message.direction === "OUTGOING" ? "você" : contactName}
        </p>

        <p className="truncate text-sm text-gray-700 dark:text-gray-300">
          {message.messageType === "text" && message.messageBody}
          {message.messageType === "document" && "📎 Documento enviado"}
          {message.messageType === "audio" && "🎤 Mensagem de áudio"}
        </p>
      </div>

      <button
        onClick={onRemove}
        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-red-500 dark:hover:bg-gray-700"
        title="Remover resposta"
      >
        ✕
      </button>
    </div>
  </div>
);

/**
 * Caixa de mensagem da conversa aberta. Só desenha: o estado e as ações vêm de
 * useMessageComposer (composer) e useAudioRecorder (recorder).
 */
const MessageComposer = ({ selectedChat, composer, recorder, quickMessages, attachments }) => {
  const {
    payload,
    updatePayload,
    activePanel,
    togglePanel,
    sendingMessage,
    uploadingAttachment,
    messageInputRef,
    sendMessage,
    sendAudioMessage,
    handleKeyDown,
    uploadAttachment,
    handlePaste,
    handleDrop,
    selectQuickMessage,
    selectExistingAttachment,
    appendEmote,
    cancelScheduledMessage,
  } = composer;
  const {
    isRecording,
    recordingTime,
    audioUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    formatRecordingTime,
  } = recorder;

  const quotedMessage = payload.quoteMessageId
    ? selectedChat.messages?.find((m) => m.messageId === payload.quoteMessageId)
    : null;

  return (
    <div className="space-y-3 border-t border-gray-200 p-4 dark:border-gray-700">
      {activePanel === "quickMessages" && (
        <QuickMessageBlock quickMessages={quickMessages} onSendQuickMessage={selectQuickMessage} />
      )}

      {activePanel === "attachments" && (
        <div className="mb-3 space-y-3">
          <Dropzone onFileUpload={(file) => uploadAttachment(file)} />
          <AttachmentList attachments={attachments} onSendAttachment={selectExistingAttachment} />
        </div>
      )}

      {activePanel === "schedule" && (
        <div className="mb-3 space-y-3">
          <div className="w-full rounded-lg border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Data e Hora
            </label>
            <input
              type="datetime-local"
              value={payload.scheduleAt}
              onChange={(e) => updatePayload({ scheduleAt: e.target.value })}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full rounded-lg bg-white px-3 py-2 text-gray-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
            />
          </div>

          <ScheduledMessagesList
            scheduledMessages={selectedChat.scheduledMessages || []}
            onCancel={cancelScheduledMessage}
          />
        </div>
      )}

      {activePanel === "emotes" && (
        <div className="mb-3">
          <EmotesPanel onSelect={appendEmote} />
        </div>
      )}

      {quotedMessage && (
        <QuotedPreview
          message={quotedMessage}
          contactName={selectedChat.contact.name}
          onRemove={() => updatePayload({ quoteMessageId: null })}
        />
      )}

      {isRecording && (
        <div className="flex items-center justify-center space-x-2 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <div className="h-3 w-3 animate-pulse rounded-full bg-red-500"></div>
          <span className="text-sm font-medium text-red-600 dark:text-red-400">
            Gravando... {formatRecordingTime(recordingTime)}
          </span>
        </div>
      )}

      {uploadingAttachment && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
          <Paperclip className="h-4 w-4 animate-pulse" />
          Anexando arquivo…
        </div>
      )}

      {payload.attachment && !audioUrl && (
        <AttachmentPreview
          attachment={payload.attachment}
          onEdit={() => togglePanel("attachments")}
          onRemove={() => updatePayload({ attachment: null })}
        />
      )}

      {payload.scheduleAt && (
        <ScheduleBadge
          scheduleAt={payload.scheduleAt}
          onEdit={() => togglePanel("schedule")}
          onRemove={() => updatePayload({ scheduleAt: "" })}
        />
      )}

      {!audioUrl ? (
        <div className="flex items-center space-x-2">
          <div className="grid grid-cols-2 gap-2">
            {inputButtons.map(({ key, title, Icon }) => (
              <button
                key={key}
                onClick={() => togglePanel(key)}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                title={title}
              >
                <Icon className="h-5 w-5" />
              </button>
            ))}
          </div>

          <textarea
            ref={messageInputRef}
            placeholder="Digite uma mensagem..."
            className="flex-1 rounded-lg bg-gray-100 px-4 py-2 text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
            value={payload.text}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onChange={(e) => updatePayload({ text: e.target.value })}
            onKeyDown={handleKeyDown}
            disabled={sendingMessage || isRecording}
            rows={3}
          ></textarea>

          {!isRecording ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={startRecording}
                className="rounded-lg bg-blue-500 p-2 text-white transition-colors hover:bg-blue-600"
                title="Gravar áudio"
                disabled={sendingMessage}
              >
                <Mic className="h-5 w-5" />
              </button>

              <button
                onClick={sendMessage}
                className="rounded-lg bg-green-500 p-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  (!payload.text.trim() && !payload.attachment) ||
                  sendingMessage ||
                  uploadingAttachment ||
                  isRecording
                }
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={stopRecording}
              className="rounded-lg bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
              title="Parar gravação"
            >
              <StopCircle className="h-5 w-5" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center space-x-3 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
          <div className="flex-1">
            <audio controls className="w-full">
              <source src={audioUrl} type="audio/webm" />
              Seu navegador não suporta o player de áudio.
            </audio>
          </div>
          <button
            onClick={cancelRecording}
            className="rounded-lg bg-red-500 p-2 text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            title="Cancelar gravação"
          >
            <Trash2 className="h-5 w-5" />
          </button>
          <button
            onClick={sendAudioMessage}
            className="rounded-lg bg-green-500 p-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={sendingMessage}
            title="Enviar áudio"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageComposer;
